import styled from "@emotion/styled";
import { Field } from "formik";
import isPropValid from "@emotion/is-prop-valid";
import { tokens } from "../../../design-system/tokens";

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.xs};
  width: 100%;
  min-width: 0;
`;

export const Label = styled.label<{ $required?: boolean }>`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.basics[0]};
  font-family: ${tokens.typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing.xs};

  ${({ $required }) =>
    $required &&
    `
      &::after {
        content: ' *';
        color: inherit;
      }
    `}
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  z-index: 1;
`;

export const StyledInput = styled(Field, {
  shouldForwardProp: (prop) =>
    isPropValid(prop) && prop !== "$hasHelpIcon",
})<{ $hasHelpIcon?: boolean }>`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  border: 1px solid ${tokens.colors.inputBackground};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.blue[900]};
  background-color: ${tokens.colors.inputBackground};
  transition: ${tokens.transitions.fast};
  font-weight: ${tokens.typography.fontWeight.semibold};
  font-family: ${tokens.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.secondary[500]};
    box-shadow: 0 0 0 2px ${tokens.colors.blue[200]};

    &::placeholder {
      color: ${tokens.colors.placeholder};
    }

    &:disabled {
      background-color: ${tokens.colors.basics[400]};
      color: ${tokens.colors.basics[1100]};
      cursor: not-allowed;
      opacity: 0.2;
    }

  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover {
    -webkit-text-fill-color: ${tokens.colors.blue[900]} !important;
    -moz-text-fill-color: ${tokens.colors.blue[900]};
    caret-color: ${tokens.colors.blue[900]};
    -webkit-box-shadow:
      0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
    box-shadow: 0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
  }

  &:-webkit-autofill:focus {
    border-color: ${tokens.colors.secondary[500]};
    -webkit-box-shadow:
      0 0 0 2px ${tokens.colors.blue[200]},
      0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
    box-shadow:
      0 0 0 2px ${tokens.colors.blue[200]},
      0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
  }

  &:autofill {
    -moz-text-fill-color: ${tokens.colors.blue[900]};
    caret-color: ${tokens.colors.blue[900]};
    box-shadow: 0 0 0 1000px ${tokens.colors.inputBackground} inset;
  }

  &:-moz-autofill {
    -moz-text-fill-color: ${tokens.colors.blue[900]};
    caret-color: ${tokens.colors.blue[900]};
    box-shadow: 0 0 0 1000px ${tokens.colors.inputBackground} inset;
  }

  &:autofill:focus {
    border-color: ${tokens.colors.secondary[500]};
    box-shadow:
      0 0 0 2px ${tokens.colors.blue[200]},
      0 0 0 1000px ${tokens.colors.inputBackground} inset;
  }

  &:-moz-autofill:focus {
    border-color: ${tokens.colors.secondary[500]};
    box-shadow:
      0 0 0 2px ${tokens.colors.blue[200]},
      0 0 0 1000px ${tokens.colors.inputBackground} inset;
  }
`;

export const DateInputContainer = styled.div<{ $hasHelpIcon?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0 ${tokens.spacing.md};
  box-sizing: border-box;
  height: 40px;
  border-radius: ${tokens.borderRadius.sm};
  background-color: ${tokens.colors.inputBackground};
  transition: ${tokens.transitions.fast};
  font-family: ${tokens.typography.fontFamily.primary};

  border: 1px solid ${tokens.colors.inputBackground};

  &:hover:not(:has(:disabled)) {
    border: 3px solid ${tokens.colors.basics[500]};
  }

  &:focus-within {
    outline: none;
    border-color: ${tokens.colors.secondary[500]};
    box-shadow: 0 0 0 2px ${tokens.colors.blue[200]};
  }

  &:has(:disabled) {
    cursor: not-allowed;
    opacity: 0.3;
    border: 1px solid ${tokens.colors.inputBackground};
  }
`;


export const DateInput = styled.input`
  border: none;
  outline: none;
  text-align: center;
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.blue[900]};
  background: transparent;
  font-weight: ${tokens.typography.fontWeight.semibold};
  font-family: ${tokens.typography.fontFamily.primary};
  min-width: 0;
  max-height: 21px;

  &::placeholder {
    color: ${tokens.colors.placeholder};
  }

  &:disabled {
    color: ${tokens.colors.basics[1100]};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${tokens.colors.blue[900]} !important;
    caret-color: ${tokens.colors.blue[900]};
    -webkit-box-shadow:
      0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
    box-shadow:
      0 0 0 1000px ${tokens.colors.inputBackground} inset !important;
  }

  &:autofill,
  &:autofill:hover,
  &:autofill:focus,
  &:-moz-autofill,
  &:-moz-autofill:focus {
    -moz-text-fill-color: ${tokens.colors.blue[900]};
    caret-color: ${tokens.colors.blue[900]};
    box-shadow: 0 0 0 1000px ${tokens.colors.inputBackground} inset;
  }
`;

export const DateSeparator = styled.span`
  color: ${tokens.colors.blue[900]};
  font-weight: ${tokens.typography.fontWeight.semibold};
  font-family: ${tokens.typography.fontFamily.primary};
  user-select: none;
`;

export const HelpOverlay = styled.span`
  position: absolute;
  right: ${tokens.spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  z-index: 2;
  align-items: center;
`;

export const HelpArea = styled.span`
  margin-top: 0.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;

  svg,
  svg * {
    stroke: #0b3e5f;
  }

  svg path[fill]:not([fill="none"]) {
    fill: #0b3e5f;
  }
`;
